import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { fi } from 'date-fns/locale'

import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { useOrganization } from '../hooks/useOrganization'
import { apiClient } from '../api/client'

interface Account {
  code: string
  name: string
  type: string
  parentAccount: string | null
  totalDebit: number
  totalCredit: number
  balance: number
  hasEntries: boolean
}

interface AccountEntry {
  id: string
  journalEntryId: string
  date: string
  description: string
  debit: number
  credit: number
  voucherNumber: number | null
  transactionId: string | null
  entryType: string | null
  balance: number
}

interface AccountDetails {
  code: string
  name: string
  type: string
  parentAccount: string | null
  description: string | null
  openingBalance: number
}

interface AccountEntriesResponse {
  account: AccountDetails
  entries: AccountEntry[]
  summary: {
    totalDebit: number
    totalCredit: number
    openingBalance: number
    closingBalance: number
  }
}

export default function GeneralLedgerPage() {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganization()

  // Filter states
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [search, setSearch] = useState('')
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null)

  // Data states
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountDetails, setAccountDetails] = useState<AccountEntriesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingEntries, setLoadingEntries] = useState(false)
  const [exporting, setExporting] = useState<'csv' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchAccounts = useCallback(async () => {
    if (!currentOrganization) return

    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      params.set('organization_id', currentOrganization.id)
      if (startDate) params.set('from', startDate)
      if (endDate) params.set('to', endDate)
      if (search) params.set('search', search)

      const response = await apiClient.get<{ accounts: Account[] }>(`/general-ledger?${params.toString()}`)
      setAccounts(response.accounts)
    } catch {
      setError(t('generalLedger.errorDescription') || 'Failed to load general ledger')
    } finally {
      setLoading(false)
    }
  }, [currentOrganization, startDate, endDate, search, t])

  const fetchAccountEntries = useCallback(async (accountCode: string) => {
    if (!currentOrganization) return

    setLoadingEntries(true)
    try {
      const params = new URLSearchParams()
      params.set('organization_id', currentOrganization.id)
      if (startDate) params.set('from', startDate)
      if (endDate) params.set('to', endDate)

      const response = await apiClient.get<AccountEntriesResponse>(`/general-ledger/${accountCode}?${params.toString()}`)
      setAccountDetails(response)
    } catch {
      setError(t('generalLedger.errorEntries') || 'Failed to load account entries')
    } finally {
      setLoadingEntries(false)
    }
  }, [currentOrganization, startDate, endDate, t])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  useEffect(() => {
    if (selectedAccount) {
      fetchAccountEntries(selectedAccount)
    } else {
      setAccountDetails(null)
    }
  }, [selectedAccount, fetchAccountEntries])

  const handleExport = async () => {
    if (!currentOrganization) return

    setExporting('csv')
    setError(null)

    try {
      const params = new URLSearchParams()
      params.set('organization_id', currentOrganization.id)
      params.set('format', 'csv')
      if (startDate) params.set('from', startDate)
      if (endDate) params.set('to', endDate)

      const response = await apiClient.get<string>(`/general-ledger/export?${params.toString()}`)

      // Download the CSV
      const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `paakirja_${format(new Date(), 'yyyy-MM-dd')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      setError(t('generalLedger.exportFailed') || 'Export failed')
    } finally {
      setExporting(null)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd.MM.yyyy', { locale: fi })
    } catch {
      return dateString
    }
  }

  const getAccountTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      asset: 'Vastaavaa',
      liability: 'Vastattavaa',
      equity: 'Oma pääoma',
      income: 'Tuotot',
      expense: 'Kulut',
    }
    return labels[type] || type
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">
          {t('generalLedger.title') || 'Pääkirja'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('generalLedger.description') || 'Kaikki kirjaukset tileittäin'}
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('generalLedger.startDate') || 'Alkaen'}
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('generalLedger.endDate') || 'Päättyen'}
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('generalLedger.search') || 'Haku'}
            </label>
            <Input
              type="text"
              placeholder={t('generalLedger.searchPlaceholder') || 'Tilinumero tai nimi...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="secondary"
              onClick={fetchAccounts}
              disabled={loading}
            >
              {loading ? t('common.loading') || 'Ladataan...' : t('common.refresh') || 'Päivitä'}
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={exporting !== null || accounts.length === 0}
            >
              {exporting ? t('common.exporting') || 'Viedään...' : t('common.exportCSV') || 'Vie CSV'}
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List */}
        <div className="lg:col-span-1">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold">{t('generalLedger.accounts') || 'Tilikartta'}</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {accounts.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {t('generalLedger.noAccounts') || 'Ei tilejä'}
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {t('generalLedger.account') || 'Tili'}
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('generalLedger.balance') || 'Saldo'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {accounts.map((account) => (
                      <tr
                        key={account.code}
                        className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                          selectedAccount === account.code ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        } ${!account.hasEntries ? 'opacity-50' : ''}`}
                        onClick={() => setSelectedAccount(account.code)}
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium">{account.code}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">{account.name}</div>
                          <Badge variant="neutral" className="text-xs mt-1">
                            {getAccountTypeLabel(account.type)}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatCurrency(account.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>

        {/* Account Entries */}
        <div className="lg:col-span-2">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold">
                {accountDetails
                  ? `${accountDetails.account.code} - ${accountDetails.account.name}`
                  : t('generalLedger.selectAccount') || 'Valitse tili nähdäksesi kirjaukset'}
              </h2>
            </div>

            {loadingEntries ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
                <p className="mt-2 text-gray-600">{t('common.loading') || 'Ladataan...'}</p>
              </div>
            ) : accountDetails ? (
              <div>
                {/* Summary */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('generalLedger.openingBalance') || 'Alkusaldo'}
                    </div>
                    <div className="font-mono font-medium">
                      {formatCurrency(accountDetails.summary.openingBalance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('generalLedger.totalDebit') || 'Debit'}
                    </div>
                    <div className="font-mono font-medium text-green-600">
                      {formatCurrency(accountDetails.summary.totalDebit)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('generalLedger.totalCredit') || 'Credit'}
                    </div>
                    <div className="font-mono font-medium text-red-600">
                      {formatCurrency(accountDetails.summary.totalCredit)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t('generalLedger.closingBalance') || 'Loppusaldo'}
                    </div>
                    <div className="font-mono font-medium">
                      {formatCurrency(accountDetails.summary.closingBalance)}
                    </div>
                  </div>
                </div>

                {/* Entries Table */}
                <div className="max-h-[500px] overflow-y-auto">
                  {accountDetails.entries.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      {t('generalLedger.noEntries') || 'Ei kirjauksia valitulla aikavälillä'}
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.date') || 'Pvm'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.voucher') || 'Tosite'}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.description') || 'Selite'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.debit') || 'Debit'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.credit') || 'Credit'}
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            {t('generalLedger.balance') || 'Saldo'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {accountDetails.entries.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-2 whitespace-nowrap">
                              {formatDate(entry.date)}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              {entry.voucherNumber || '-'}
                            </td>
                            <td className="px-4 py-2">
                              {entry.description}
                              {entry.entryType && entry.entryType !== 'normal' && (
                                <Badge variant="neutral" className="ml-2 text-xs">
                                  {entry.entryType}
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {entry.debit > 0 ? formatCurrency(entry.debit) : ''}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {entry.credit > 0 ? formatCurrency(entry.credit) : ''}
                            </td>
                            <td className="px-4 py-2 text-right font-mono font-medium">
                              {formatCurrency(entry.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                {t('generalLedger.selectAccountPrompt') || 'Valitse tili vasemmalta nähdäksesi kirjaukset'}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
